import React, { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { mediaUrl, kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * Signature interactive infinite product carousel.
 * mode="auto": seamless looping drift; edge-hover direction control; hover highlight+scale; arrows; touch swipe.
 * mode="focus": no drift; focusId product centered & highlighted; manual edge/arrows; mouse-leave returns to focus.
 * Rendered as ONE reusable component (used on home, category & product pages via GlobalProductBanner).
 */
export function ProductCarousel({ products = [], mode = "auto", focusId = null }) {
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const containerRef = useRef(null);
  const trackRef = useRef(null);

  const offsetRef = useRef(0);
  const oneSetRef = useRef(0);
  const minOffsetRef = useRef(0);
  const stepRef = useRef(220);
  const focusTargetRef = useRef(0);

  const baseVel = -0.5;            // slow leftward drift
  const edgeSpeed = 3.6;
  const hoverPausedRef = useRef(false);
  const edgeDirRef = useRef(0);    // -1 => content moves left (next), +1 => content moves right (prev)
  const returnRef = useRef(false);
  const tweenRef = useRef(false);
  const tweenTargetRef = useRef(0);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffRef = useRef(0);
  const movedRef = useRef(false);
  const rafRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [ready, setReady] = useState(false);

  // How many copies to render for a seamless auto loop.
  const copies = useMemo(() => {
    if (mode !== "auto" || products.length === 0) return 1;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1440;
    const oneEst = products.length * 116;
    return Math.max(2, Math.ceil((vw * 2) / oneEst) + 1);
  }, [mode, products.length]);

  const items = useMemo(() => {
    if (mode !== "auto" || products.length === 0) return products;
    return Array.from({ length: copies }).flatMap(() => products);
  }, [mode, products, copies]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const measure = useCallback(() => {
    const track = trackRef.current, cont = containerRef.current;
    if (!track || !cont) return;
    const first = track.children[0];
    if (first) stepRef.current = first.offsetWidth + 16;
    if (mode === "auto") {
      oneSetRef.current = track.scrollWidth / copies;
    } else {
      const totalW = track.scrollWidth;
      minOffsetRef.current = Math.min(0, cont.clientWidth - totalW);
      const el = track.querySelector('[data-focus="true"]');
      if (el) {
        const t = -(el.offsetLeft - cont.clientWidth / 2 + el.clientWidth / 2);
        focusTargetRef.current = clamp(t, minOffsetRef.current, 0);
        returnRef.current = true; // animate into place
      }
    }
  }, [mode, copies]);

  useEffect(() => {
    setReady(false);
    offsetRef.current = 0;
    const id = setTimeout(() => { measure(); setReady(true); }, 60);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    const ro = trackRef.current && typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure()) : null;
    if (ro) ro.observe(trackRef.current);
    return () => { clearTimeout(id); window.removeEventListener("resize", onResize); ro?.disconnect(); };
  }, [measure, products, focusId]);

  useEffect(() => {
    const frame = () => {
      const track = trackRef.current;
      if (track) {
        let off = offsetRef.current;
        if (draggingRef.current) {
          // set by pointer move
        } else if (tweenRef.current) {
          off += (tweenTargetRef.current - off) * 0.18;
          if (Math.abs(tweenTargetRef.current - off) < 0.5) { off = tweenTargetRef.current; tweenRef.current = false; }
        } else if (mode === "focus") {
          if (edgeDirRef.current !== 0 && !reduced) {
            off += edgeDirRef.current * edgeSpeed;
          } else if (returnRef.current) {
            off += (focusTargetRef.current - off) * 0.12;
            if (Math.abs(focusTargetRef.current - off) < 0.5) { off = focusTargetRef.current; returnRef.current = false; }
          }
          off = clamp(off, minOffsetRef.current, 0);
        } else {
          let v;
          if (hoverPausedRef.current) v = 0;
          else if (edgeDirRef.current !== 0) v = edgeDirRef.current * edgeSpeed;
          else v = reduced ? 0 : baseVel;
          off += v;
          const one = oneSetRef.current;
          if (one > 0) {
            if (off <= -one) off += one;
            if (off > 0) off -= one;
          }
        }
        offsetRef.current = off;
        track.style.transform = `translate3d(${off}px,0,0)`;
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [mode, reduced]);

  const startTween = (delta) => {
    tweenTargetRef.current = mode === "focus"
      ? clamp(offsetRef.current + delta, minOffsetRef.current, 0)
      : offsetRef.current + delta;
    tweenRef.current = true;
    returnRef.current = false;
  };
  const arrow = (dir) => startTween(dir * stepRef.current * 2); // dir +1 => reveal previous

  // Edge zone handlers (mouse only for auto-scroll on hover).
  const enterEdge = (dir) => (e) => { if (e.pointerType !== "touch") edgeDirRef.current = dir; };
  const leaveEdge = () => { edgeDirRef.current = 0; };

  const onBandLeave = (e) => {
    edgeDirRef.current = 0;
    hoverPausedRef.current = false;
    if (mode === "focus" && e.pointerType !== "touch") returnRef.current = true;
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse") return; // desktop uses hover/edge/arrows, not drag
    draggingRef.current = true;
    movedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartOffRef.current = offsetRef.current;
    tweenRef.current = false;
    returnRef.current = false;
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - dragStartXRef.current;
    if (Math.abs(dx) > 6) movedRef.current = true;
    let off = dragStartOffRef.current + dx;
    if (mode === "focus") off = clamp(off, minOffsetRef.current, 0);
    else {
      const one = oneSetRef.current;
      if (one > 0) { while (off <= -one) off += one; while (off > 0) off -= one; }
    }
    offsetRef.current = off;
  };
  const onPointerUp = () => { draggingRef.current = false; };

  const openProduct = (p) => (e) => {
    if (movedRef.current) { movedRef.current = false; return; }
    navigate(`/produkter/${p.slug}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); arrow(1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); arrow(-1); }
  };

  if (!products.length) return null;

  return (
    <div className="relative select-none" data-testid="product-carousel" data-mode={mode}>
      {/* Left edge zone + arrow */}
      <div
        onPointerEnter={enterEdge(1)} onPointerLeave={leaveEdge}
        className="absolute left-0 top-0 z-20 flex h-full w-[13%] items-center justify-start pl-1"
      >
        <button
          data-testid="carousel-arrow-left" aria-label="Scroll venstre" onClick={() => arrow(1)}
          className="pointer-events-auto grid h-10 w-10 place-items-center rounded-full border border-border bg-background/85 text-foreground shadow-md backdrop-blur transition-all hover:bg-primary hover:text-primary-foreground"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>
      {/* Right edge zone + arrow */}
      <div
        onPointerEnter={enterEdge(-1)} onPointerLeave={leaveEdge}
        className="absolute right-0 top-0 z-20 flex h-full w-[13%] items-center justify-end pr-1"
      >
        <button
          data-testid="carousel-arrow-right" aria-label="Scroll højre" onClick={() => arrow(-1)}
          className="pointer-events-auto grid h-10 w-10 place-items-center rounded-full border border-border bg-background/85 text-foreground shadow-md backdrop-blur transition-all hover:bg-primary hover:text-primary-foreground"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div
        ref={containerRef}
        role="listbox"
        aria-label="Produkt-carousel"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerLeave={onBandLeave}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="overflow-hidden py-3"
        style={{ opacity: ready ? 1 : 0, transition: "opacity .35s", touchAction: "pan-y" }}
      >
        <div ref={trackRef} className="flex items-start gap-2.5 will-change-transform" style={{ width: "max-content" }}>
          {items.map((p, i) => {
            const isFocus = mode === "focus" && p.id === focusId;
            return (
              <div
                key={`${p.id}-${i}`}
                data-focus={isFocus ? "true" : "false"}
                data-testid={`carousel-item-${p.slug}`}
                role="option"
                aria-selected={isFocus}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/produkter/${p.slug}`); }}
                onPointerEnter={(e) => { if (e.pointerType === "mouse") hoverPausedRef.current = true; }}
                onPointerLeave={(e) => { if (e.pointerType === "mouse" && !draggingRef.current) hoverPausedRef.current = false; }}
                onClick={openProduct(p)}
                className={`b1m-carousel-card group relative w-[68px] shrink-0 cursor-pointer rounded-xl transition-transform duration-300 ease-out sm:w-[78px] lg:w-[88px] ${
                  isFocus ? "z-20 scale-[1.18]" : "hover:z-20 hover:scale-[1.2]"
                }`}
              >
                <div className={`relative aspect-square overflow-hidden rounded-xl border bg-secondary shadow-sm ${isFocus ? "border-primary shadow-[0_8px_22px_rgba(236,30,121,0.35)]" : "border-border group-hover:border-primary/70 group-hover:shadow-[0_8px_20px_rgba(236,30,121,0.25)]"}`}>
                  <img src={mediaUrl(p.main_image)} alt={p.name} loading="lazy" draggable="false" className="h-full w-full object-cover" />
                  {p.on_sale && p.discount_percent > 0 && (
                    <span className="absolute left-1 top-1 rounded-full bg-primary px-1 py-0.5 text-[8px] font-bold leading-none text-primary-foreground">-{p.discount_percent}%</span>
                  )}
                  {!p.in_stock && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                      <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[8px] font-bold text-destructive-foreground">UDSOLGT</span>
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-1 pb-1 pt-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <span className="line-clamp-2 text-[8px] font-semibold leading-tight text-white">{p.name}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); if (p.in_stock) addToCart(p, 1); }}
                    disabled={!p.in_stock}
                    data-testid={`carousel-add-${p.slug}`}
                    className="pointer-events-auto absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-primary opacity-0 shadow transition-all duration-200 hover:scale-110 group-hover:opacity-100 disabled:opacity-0"
                    aria-label="Tilføj til kurv"
                  >
                    <Plus className="h-3 w-3 text-primary-foreground" />
                  </button>
                </div>
                <p className="mt-1 text-center text-[11px] font-extrabold leading-none text-primary">{kr(p.effective_price)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
